/**
 * EventDetail — Conference-style event landing page
 * Deep-link URL: /events/:slug
 * Obsidian Pulse design: dark #080C18, accent colors per event, Bebas Neue headings.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { isPastCalendarEvent } from "@/lib/calendarFilters";
import { useEventClock } from "@/hooks/useEventClock";
import { useParams, Link, useLocation } from "wouter";
import { MY_TICKETS_TAB_KEY, ticketsCheckoutEnabled } from "@/lib/eventCta";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CtaIcon from "@/components/CtaIcon";
import { chiNotify } from "@/components/ChiNotification";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { getEventBySlug, slugToEventUrl, type CHIEvent } from "@/lib/eventsData";
import { paidEventCatalogRefetchInterval } from "@/lib/eventCatalogFreshness";
import { getInternalEventRegistrationCreateAccountUrl, getInternalEventRegistrationLoginUrl, getPostRegistrationRedirectUrl } from "@/lib/eventRegistration";
import { PENDING_REVIEW_MESSAGE, resolveEventRegistrationAction } from "@/lib/eventRegistrationFlow";
import { getEventTicketsUrl, shouldUseTicketsPage } from "@/lib/eventRegistrationFlow";
import { resolveTicketCardState, ticketCardPriceText } from "@/lib/ticketRegistrationCard";
import { useMyTicketsForEvent } from "@/hooks/useMyTicketsForEvent";
import { useServerNow } from "@/lib/serverClock";
import { EventTicketsPanel, TicketRegistrationCta } from "@/components/tickets/EventTicketsPanel";
import { shouldShowBuyTicketsCta } from "@/components/tickets/buyTicketsCta";
// CompleteTicketPurchase (temporary)
import { completePurchaseEligibleFrom, shouldShowCompleteTicketPurchase } from "@/components/CompleteTicketPurchase";
import { formatCents } from "@/components/tickets/TicketPurchaseParts";
import {
  closeReservedCheckoutWindow,
  handoffToCheckout,
  reserveCheckoutWindow,
  type ReservedCheckoutWindow,
} from "@/lib/checkoutWindow";
import { trpc } from "@/lib/trpc";
import { isApprovalQueueEvent, presentRegisterFailure, useMyRegistrationStatus } from "@/lib/registrationApproval";
import { personInitials } from "@/lib/personDisplay";
import { eventFeaturedArtwork } from "@shared/eventPlaceholderArt";
import SocialShareButtons from "@/components/SocialShareButtons";
import MediaCarousel from "@/components/MediaCarousel";
import EventVideoPlayer from "@/components/EventVideoPlayer";
import EventDocumentList from "@/components/EventDocumentList";
import EventMediaHeading from "@/components/EventMediaHeading";
import MembershipLockNotice from "@/components/MembershipLockNotice";
import EventMediaEmptyState from "@/components/EventMediaEmptyState";
import ResponsiveImage from "@/components/ResponsiveImage";
import { WAITLIST_REGISTRANT_COPY } from "@shared/registrationWaitlist";
import { ArrowLeft, Building2, Calendar, CalendarCheck, CalendarPlus, CheckCheck, Clock, Copy, Download, ExternalLink, FileText, Images, Info, LockKeyhole, MapPin, PlayCircle, Share2, Star, Ticket, UserRound, Users, Video } from "lucide-react";

/**
 * A speaker's face, or their initials when there is no usable photo.
 *
 * Every speaker gets one. The portrait used to be conditional, so a speaker without a
 * photo left an empty space where a person should be — and the page then looked
 * broken rather than merely incomplete.
 *
 * `failed` covers the second way this goes wrong: a portrait URL that exists but does
 * not load. Without it a dead link renders the browser's broken-image glyph, which is
 * worse than initials because it reads as our bug rather than as a missing upload.
 *
 * Fluid rather than pixel-sized, unlike `PersonPortraitAvatar` in the CRM: the hero
 * ring scales with the viewport, so the face has to fill its container instead of
 * being handed a number.
 */
function SpeakerFace({
  name,
  portraitUrl,
  accent,
  radius,
}: {
  name: string;
  portraitUrl?: string | null;
  accent: string;
  /** Matches the frame it sits in — a circle in the hero, a rounded square in the card. */
  radius: string;
}) {
  const [failed, setFailed] = useState(false);
  const showPhoto = Boolean(portraitUrl) && !failed;

  if (showPhoto) {
    return (
      <ResponsiveImage
        src={portraitUrl as string}
        alt={name}
        onError={() => setFailed(true)}
        maxWidth={320}
        sizes="280px"
        style={{ width: "100%", height: "100%", borderRadius: radius, objectFit: "cover", objectPosition: "center 18%", display: "block" }}
      />
    );
  }

  return (
    <span
      aria-label={name}
      role="img"
      style={{
        display: "grid",
        placeItems: "center",
        width: "100%",
        height: "100%",
        // Makes cqmin below resolve against this box rather than the viewport.
        containerType: "size",
        borderRadius: radius,
        /*
          Near-neutral, with only a trace of the accent — the same restraint as the
          glass ring around it. A saturated placeholder drew more attention than the
          real portraits it stands in for.
        */
        background: `linear-gradient(150deg, ${accent}1F, rgba(17,22,40,.96))`,
        /*
          Half-opacity, and large enough to fill the frame: the initials are a
          placeholder standing in for a face, so they read better as a soft watermark
          filling the space than as small solid text floating in the middle of it.
        */
        color: "rgba(248,250,252,0.5)",
        fontFamily: "'Space Grotesk', sans-serif",
        /*
          A share of the container rather than a fixed size, so the same component
          fills a 280px hero ring and a 104px card. cqmin needs the container to
          declare containment, which the line below does.
        */
        // 4em against the container's own font size, capped by the box so the two
        // capitals stay inside the rim in the smaller card as well as the hero ring.
        fontSize: "4em",
        fontWeight: 700,
        /*
          Optically centred, not just box-centred. `lineHeight: 1` collapses the line
          box to the font size so grid centring has nothing extra to centre against,
          and the block display keeps the glyphs' own baseline from adding a descender
          gap that would sit the letters slightly high.
        */
        lineHeight: 1,
        textAlign: "center",
        letterSpacing: "0.02em",
        /*
          `personInitials` returns at most two characters, so this should never
          trigger — but a clipped glyph inside the circle beats one drawn across the
          rim if that ever stops being true.
        */
        overflow: "hidden",
        // Keeps the text off the curve at the left and right extremes.
        padding: "0 12%",
      }}
    >
      {personInitials(name)}
    </span>
  );
}

export default function EventDetail() {
  const params = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const slug = params.slug ?? "";
  const managedEventsQuery = trpc.events.publicCatalog.useQuery(undefined, {
    retry: false,
    refetchInterval: paidEventCatalogRefetchInterval,
    refetchOnWindowFocus: true,
  });
  const catalogEvent = getEventBySlug(slug);
  const managedEvent = useMemo(
    () => ((managedEventsQuery.data ?? []) as CHIEvent[]).find(item => item.slug === slug),
    [managedEventsQuery.data, slug],
  );
  const event = useMemo(
    () => managedEvent ?? catalogEvent,
    [catalogEvent, managedEvent],
  );
  const eventGraphicQuery = trpc.events.publicGraphic.useQuery(
    { eventSlug: slug },
    { enabled: Boolean(slug), retry: false },
  );
  /*
   * Flyers the event's speakers uploaded for this talk. Public and unauthenticated:
   * the graphic is promotion for an event that is already listed, so it is shown
   * to whoever lands on the page rather than only to signed-in members.
   */
  const speakerGraphicsQuery = trpc.speakers.publicEventGraphics.useQuery(
    { eventSlug: slug },
    { enabled: Boolean(slug), retry: false },
  );
  const speakerGraphics = speakerGraphicsQuery.data ?? [];
  /*
   * Documents attached by an administrator — the deck, the handout. Titles are
   * public; the list decides per viewer whether a row is a download or a lock.
   */
  const eventDocumentsQuery = trpc.events.publicDocuments.useQuery(
    { eventSlug: slug },
    { enabled: Boolean(slug), retry: false },
  );
  const eventDocuments = eventDocumentsQuery.data ?? [];
  /*
   * Published speaker portraits, for the face this page shows. Same source the public
   * Speakers page reads, so an approved portrait appears in both places rather than
   * only where it was uploaded.
   */
  const publishedSpeakersQuery = trpc.speakers.publicList.useQuery(undefined, { staleTime: 30_000, retry: false });
  const [copied, setCopied] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [heroSpeakerIndex, setHeroSpeakerIndex] = useState(0);
  const [heroSpeakerPaused, setHeroSpeakerPaused] = useState(false);
  const checkoutWindowRef = useRef<ReservedCheckoutWindow | null>(null);
  const membershipAuth = useOptionalAuth();
  const isLoggedIn = membershipAuth?.isLoggedIn ?? false;
  const isPremium = membershipAuth?.isPremium ?? false;
  const isAuthenticated = isLoggedIn;
  // This intentionally reveals only presence to the public page. The resource
  // list and protected download URLs remain behind the member endpoint.
  const resourceAvailabilityQuery = trpc.events.resourceAvailability.useQuery(
    { eventSlug: slug },
    { enabled: Boolean(slug), retry: false },
  );
  const hasResources = resourceAvailabilityQuery.data?.hasResources === true;
  const memberResourcesQuery = trpc.events.memberResources.useQuery(
    { eventSlug: slug },
    { enabled: Boolean(slug) && hasResources && isLoggedIn && isPremium, retry: false },
  );
  const memberResources = memberResourcesQuery.data as unknown as {
    access: "granted" | "locked";
    resources: Array<{
      id: number;
      kind: "document" | "video";
      label: string;
      contentType: string | null;
      sizeBytes: number | null;
      videoUrl: string | null;
      providerTitle: string | null;
      downloadUrl: string | null;
    }>;
  } | undefined;
  /*
   * The Media & resources tab — recording, documents, flyers — is for paid members.
   * Everyone still sees the tab, with a lock where its count would be.
   */
  const canAccessMedia = membershipAuth?.isPremium ?? false;
  const trpcUtils = trpc.useUtils();
  const myRegistrationStatus = useMyRegistrationStatus(slug, { enabled: isLoggedIn && Boolean(event) });
  const myRegistrationsQuery = trpc.events.myRegistrations.useQuery(undefined, {
    enabled: isLoggedIn,
    retry: false,
  });
  const pricingPolicyQuery = trpc.events.publicPricingPolicy.useQuery(
    { eventSlug: slug },
    { enabled: Boolean(event), retry: false },
  );
  const reapplyMutation = trpc.events.reapply.useMutation({
    onSuccess: result => {
      if (result.status !== "pending_review") return;
      setRegistrationError(null);
      chiNotify.info(PENDING_REVIEW_MESSAGE, {
        description: `${event?.title ?? "This event"} is reviewed by the organizers before your place is confirmed.`,
        dedupeKey: `event-reapply-${event?.slug ?? "detail"}`,
      });
      void trpcUtils.events.myRegistrations.invalidate();
    },
    onError: error => {
      const message = error.message || "We could not submit your registration. Please try again.";
      setRegistrationError(message);
      chiNotify.error("Registration could not be submitted", {
        description: message,
        dedupeKey: `event-reapply-error-${event?.slug ?? "detail"}`,
      });
    },
  });
  const registrationMutation = trpc.events.register.useMutation({
    onSuccess: result => {
      if (result.status === "external_checkout_required" && result.checkoutUrl) {
        chiNotify.info("Continue to secure registration", {
          description: "Opening the secure event checkout in a new tab.",
          dedupeKey: `event-checkout-${event?.slug ?? "detail"}`,
        });
        const reservedWindow = checkoutWindowRef.current;
        checkoutWindowRef.current = null;
        handoffToCheckout(result.checkoutUrl, reservedWindow);
        return;
      }
      closeReservedCheckoutWindow(checkoutWindowRef.current);
      checkoutWindowRef.current = null;
      if (result.status === "unavailable") {
        const message = result.reason ?? "Registration is not currently available for this event.";
        setRegistrationError(message);
        chiNotify.warning("Registration is unavailable", {
          description: message,
          dedupeKey: `event-unavailable-${event?.slug ?? "detail"}`,
        });
        void trpcUtils.events.myRegistrations.invalidate();
        return;
      }
      setRegistrationError(null);
      if (result.status === "pending_review") {
        chiNotify.info("Registration received", {
          description: "Your registration is under review. We’ll let you know as soon as it’s confirmed.",
          dedupeKey: `event-registration-review-${event?.slug ?? "detail"}`,
        });
        void trpcUtils.events.myRegistrations.invalidate();
        return;
      }
      chiNotify.success(
        result.status === "already_registered" ? "You’re already registered" : "Registration confirmed",
        {
          description: `${event?.title ?? "This event"} is saved in My Events.`,
          dedupeKey: `event-registration-${event?.slug ?? "detail"}`,
        },
      );
      void trpcUtils.events.myRegistrations.invalidate();
      void trpcUtils.events.myZoomJoinLink.invalidate({ eventSlug: event?.slug ?? slug });
    },
    onError: error => {
      closeReservedCheckoutWindow(checkoutWindowRef.current);
      checkoutWindowRef.current = null;
      const presented = presentRegisterFailure(error, () => {
        void trpcUtils.events.myRegistrations.invalidate();
      });
      if (presented.kind === "blocked") {
        setRegistrationError(null);
        chiNotify.info(presented.message, { dedupeKey: `event-registration-${event?.slug ?? "detail"}` });
        return;
      }
      setRegistrationError(presented.message);
      chiNotify.error("Registration could not be completed", {
        description: presented.message,
        dedupeKey: `event-registration-error-${event?.slug ?? "detail"}`,
      });
    },
  });

  const eventUrl = slugToEventUrl(slug);

  const [savingInvite, setSavingInvite] = useState(false);
  /**
   * Whether the event has already happened.
   *
   * Uses the calendar's own rule so the page and the calendar never disagree about
   * which side of today an event falls on.
   */
  const eventClock = useEventClock();
  const eventHasPassed = useMemo(
    () => (event ? isPastCalendarEvent(event as CHIEvent, eventClock) : false),
    [event, eventClock],
  );
  /* What a past event has to offer on its Media tab: protected resources or public document metadata. */
  const hasPostEventMedia = hasResources || eventDocuments.length > 0;

  /**
   * Which content view is showing.
   *
   * "About" first, because it is why someone opens an upcoming event. A past event
   * with a recording is the exception: the recording is what the visitor came for,
   * and it used to sit behind a tab nobody thought to press — the video was
   * attached, rendered, and invisible because its panel was `display: none`.
   */
  const [contentTab, setContentTab] = useState<"description" | "speaker" | "media" | "tickets">("description");
  // CHI-20: the "My Ticket(s)" tab renders ONLY with webhook-issued active tickets (getMyTicketsForEvent).
  const myEventTickets = useMyTicketsForEvent(params.slug ?? "", isLoggedIn, true);
  const ctaServerNow = useServerNow(myEventTickets.serverNow, 30_000);
  // CHI-20 hero deep link (?tab=tickets): applied once, and only when the My Ticket(s) tab exists.
  const openedOnTicketsRef = useRef(false);
  useEffect(() => {
    if (openedOnTicketsRef.current || !myEventTickets.showTab) return;
    if (typeof window === "undefined" || new URLSearchParams(window.location.search).get("tab") !== MY_TICKETS_TAB_KEY) return;
    openedOnTicketsRef.current = true;
    setContentTab(MY_TICKETS_TAB_KEY);
  }, [myEventTickets.showTab]);

  /*
   * Applied once the event resolves rather than as the initial state: the event
   * arrives from a query, so on first render there is nothing to judge yet.
   */
  const openedOnRecordingRef = useRef(false);
  useEffect(() => {
    if (openedOnRecordingRef.current) return;
    if (!eventHasPassed || !hasPostEventMedia || !canAccessMedia) return;
    openedOnRecordingRef.current = true;
    setContentTab("media");
  }, [eventHasPassed, hasPostEventMedia, canAccessMedia]);
  const calendarInviteQuery = trpc.useUtils().events.calendarInvite;

  /**
   * Downloads the event as a .ics file.
   *
   * The file is built into a Blob and released immediately after the click. Holding
   * an object URL open would keep the whole file in memory for the life of the tab,
   * and a visitor who saves three events would keep three.
   */
  async function saveToCalendar() {
    setSavingInvite(true);
    try {
      const invite = await calendarInviteQuery.fetch({ eventSlug: slug });
      const blob = new Blob([invite.content], { type: "text/calendar;charset=utf-8" });
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = invite.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      chiNotify.success("Calendar file saved", {
        description: `${event?.title ?? "The event"} is ready to import into Outlook, Apple Calendar, or Google Calendar.`,
        dedupeKey: `event-ics-${slug}`,
      });
    } catch (error) {
      chiNotify.error("Calendar file could not be created", {
        description: error instanceof Error ? error.message : "Please try again.",
        dedupeKey: `event-ics-error-${slug}`,
      });
    } finally {
      setSavingInvite(false);
    }
  }

  function copyUrl() {
    const confirmCopy = () => {
      setCopied(true);
      chiNotify.success("Event link copied", {
        description: "The event page link is ready to share.",
        dedupeKey: `event-link-${slug}`,
      });
      setTimeout(() => setCopied(false), 2500);
    };

    navigator.clipboard?.writeText(eventUrl).then(() => {
      confirmCopy();
    }).catch(() => {
      // fallback
      const el = document.createElement("textarea");
      el.value = eventUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      confirmCopy();
    });
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(
    () => () => closeReservedCheckoutWindow(checkoutWindowRef.current),
    [],
  );

  // Hooks must run before any early return (fixes "Rendered more hooks" crash).
  const heroSpeakerEntries = (publishedSpeakersQuery.data ?? []).filter(entry => entry.eventSlug === slug);
  const heroSpeakerKey = heroSpeakerEntries.length > 0
    ? heroSpeakerEntries.map(entry => `${entry.id}:${entry.name}`).join("|")
    : `-1:${event?.speaker ?? ""}`;
  const heroSpeakerCount = heroSpeakerEntries.length > 0 ? heroSpeakerEntries.length : 1;
  useEffect(() => {
    setHeroSpeakerIndex(0);
  }, [heroSpeakerKey]);
  useEffect(() => {
    if (
      heroSpeakerCount < 2
      || heroSpeakerPaused
      || window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) return;
    const rotation = window.setInterval(() => {
      setHeroSpeakerIndex(current => (current + 1) % heroSpeakerCount);
    }, 5_000);
    return () => window.clearInterval(rotation);
  }, [heroSpeakerKey, heroSpeakerCount, heroSpeakerPaused]);

  if (!event && managedEventsQuery.isLoading) {
    return <div style={{ background: "#080C18", minHeight: "100vh" }}><Navigation /><div style={{ paddingTop: "180px", textAlign: "center", color: "#94A3B8" }}>Loading event…</div></div>;
  }

  if (!event) {
    return (
      <div style={{ background: "#080C18", minHeight: "100vh" }}>
        <Navigation />
        <div style={{ paddingTop: "180px", textAlign: "center" }}>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "3rem", color: "white" }}>Event Not Found</h1>
          <p style={{ color: "#64748B", marginBottom: "2rem" }}>This event doesn't exist or may have been removed.</p>
          <Link href="/calendar-of-events">
            <button className="chi-btn-primary">← Back to Events</button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const activeEvent = { ...event, joinUrl: myRegistrationStatus.data?.joinUrl ?? undefined };
  const isVirtual = event.type === "virtual";
  const accentColor = event.color;
  /*
   * The speaker's actual face, in order of how much it is really a portrait.
   *
   * 1. The published speaker portrait for this engagement. This is the one that went
   *    through the portrait pipeline — consented, reviewed, approved — so it is the
   *    right image and the one the Speakers page already shows.
   * 2. `portraitImage` on the catalog entry, described in eventsData as the
   *    "authentic speaker portrait for speaker-forward layouts".
   * 3. Nothing, which means initials.
   *
   * 3. The curated photo for this event, shared with the Speakers page. Most catalog
   *    speakers predate the portrait pipeline and this is the only place their
   *    photograph lives — without it an event page showed initials for a speaker
   *    whose face the site was already displaying two clicks away.
   * 4. `heroImage`, but only when it is a speaker asset.
   *
   * That last condition is the awkward one and it is deliberate. Several catalog
   * entries point `heroImage` at a genuine headshot — michael-goldberg-headshot.jpg,
   * tiona-nicholas.webp — and dropping the field outright threw those portraits away
   * and replaced real faces with initials. But HR Leaders points the same field at
   * /media/events/professional-panel.webp, a photograph of a panel, and using that
   * put an event graphic in a circle captioned with one person's name.
   *
   * The field means two different things depending on the event, so the asset's
   * location is what tells them apart: /media/speakers/ is a person, /media/events/
   * is the event. A heuristic, but a checkable one — and better than either losing
   * every portrait or captioning a crowd shot as somebody's face.
   *
   * The real fix is a portrait uploaded through the speaker pipeline, which is what
   * step 1 reads and which makes steps 2 and 3 unnecessary for that speaker.
   */
  const publishedSpeakers = publishedSpeakersQuery.data ?? [];
  const canonicalEventSpeakers = publishedSpeakers.filter(entry => entry.eventSlug === slug);
  /*
   * Canonical assignments are people, not event decoration. Keep every approved,
   * active assignment even when portrait generation is unavailable, and let
   * SpeakerFace render that person's initials when their own publication is absent.
   * The legacy single-speaker identity remains only for catalog events that have not
   * moved to canonical assignments yet, but event artwork is never used as that
   * person's portrait.
   */
  const eventSpeakers = canonicalEventSpeakers.length > 0
    ? canonicalEventSpeakers
    : [{
        id: -1,
        name: event.speaker,
        organization: event.speakerOrg,
        professionalTitle: "",
        bio: event.speakerBio ?? "",
        portraitUrl: null,
        eventSlug: slug,
      }];
  const activeHeroSpeaker = eventSpeakers[heroSpeakerIndex] ?? eventSpeakers[0];
  const eventSpeakerSummary = eventSpeakers.map(entry => entry.name).join(" & ");
  /*
   * The same decision every other surface makes, so the administrator's Event
   * Picture appears here when it appears on the calendar tile — and a legacy
   * catalog `heroImage`, often a speaker's headshot, is the backdrop on neither.
   * The rule and its reason live with eventFeaturedArtwork.
   */
  const eventHeroArtwork = eventFeaturedArtwork({
    slug: event.slug,
    title: event.title,
    eventImage: event.eventImage ?? catalogEvent?.eventImage,
    heroImage: event.heroImage,
    generatedImage: eventGraphicQuery.data?.url,
  }, { width: 1200, height: 675 });
  const pricingPolicy = pricingPolicyQuery.data;
  // G4.1: a special (approval-queue) event never offers external checkout or an external link.
  const isSpecialEvent = event.isSpecialEvent === true;
  const isExternalPaid = !isSpecialEvent && pricingPolicy?.pricingMode === "paid_external" && pricingPolicy.checkoutAvailability === "available" && Boolean(pricingPolicy.externalCheckoutUrl);
  // G4.1: a special event is reviewed, never checked out, so it never shows paid or "checkout unavailable" copy.
  const isInternalCheckoutUnavailable = !isSpecialEvent && (pricingPolicy?.pricingMode === "paid_internal" || pricingPolicy?.checkoutAvailability === "not_configured" || pricingPolicy?.checkoutAvailability === "unavailable");
  const registrationLabel = isSpecialEvent
    ? "Reviewed by the event team"
    : isExternalPaid || isInternalCheckoutUnavailable ? pricingPolicy?.priceLabel || "Paid registration" : pricingPolicy?.priceLabel || "Complimentary";
  const externalRegistrationUrl = isSpecialEvent ? undefined : getPostRegistrationRedirectUrl(event);
  const myRegistration = myRegistrationsQuery.data?.find(registration => registration.eventSlug === event.slug);
  const registered = Boolean(myRegistration?.registrationState === "registered");
  const waitlisted = Boolean(myRegistration?.registrationState === "waitlisted");
  const canReapply = Boolean(myRegistration?.canReapply);
  const registrationAction = resolveEventRegistrationAction({
    isAuthenticated: isLoggedIn,
    pricingPolicy,
    externalRegistrationUrl,
    joinUrl: activeEvent.joinUrl,
    hasEnded: eventHasPassed,
    registrationState: myRegistration?.registrationState,
    isSpecialEvent,
  });
  const approvalStatus = myRegistrationStatus.data?.status;
  /*
   * Pending review is special-only. Waitlisted is disabled only on approval-queue
   * events; a legacy waitlisted row on an ordinary event self-registers as on main.
   * #23's reapply CTA stays active when the server marks the row canReapply.
   */
  const awaitingDecision =
    registrationAction === "pending_review"
    || approvalStatus === "pending"
    || (approvalStatus === "waitlisted" && isApprovalQueueEvent(event));
  const ctaInactive = registrationAction === "has_ended" || registrationAction === "unavailable" || (awaitingDecision && !canReapply);
  const ctaBusy = registrationMutation.isPending || reapplyMutation.isPending;

  // CHI-20: one coherent registration-card state for ticketed (paid_internal) events.
  const ticketCard = resolveTicketCardState({
    pricingPolicy,
    isSpecialEvent: activeEvent.isSpecialEvent === true,
    awaitingApproval: awaitingDecision && !canReapply,
    quote: myEventTickets.quote,
    tickets: myEventTickets.activeTickets,
    finalizing: myEventTickets.finalizing,
    // Confirmed, non-disputed orders may have only part of their QR batch ready.
    hasBlockingTicket: myEventTickets.hasBlockingTicket && !myEventTickets.finalizing,
  });
  /*
   * CHI-22 (Dev1's ONE rule, eventCta.ts): signed-out viewers and signed-in members without a
   * blocking ticket see exactly one CTA, Buy Tickets. Ended is judged on the server clock when
   * the tickets read supplied one (useServerNow; local clock otherwise).
   */
  const cardBuyTicketsSlug = shouldShowBuyTicketsCta(
    {
      slug: activeEvent.slug,
      pricingMode: pricingPolicy?.pricingMode,
      isSpecialEvent: activeEvent.isSpecialEvent === true,
      hasEnded: eventHasPassed || isPastCalendarEvent(activeEvent as CHIEvent, new Date(ctaServerNow)),
      ticketsCheckoutEnabled: ticketsCheckoutEnabled(pricingPolicy),
    },
    { hasBlockingTicket: myEventTickets.hasBlockingTicket },
  ) ? activeEvent.slug : null;
  // CompleteTicketPurchase (temporary): only where Buy Tickets shows; paid (registered/finalizing) wins.
  const cardCompletePurchase = isLoggedIn && shouldShowCompleteTicketPurchase({
    showBuyTickets: cardBuyTicketsSlug !== null,
    eligible: completePurchaseEligibleFrom(myEventTickets),
    hasTicketsToView: ticketCard.kind === "registered" || ticketCard.kind === "finalizing",
  });

  function handleRegistrationAction() {
    if (canReapply) {
      setRegistrationError(null);
      reapplyMutation.mutate({ eventSlug: activeEvent.slug });
      return;
    }
    if (awaitingDecision) return;
    setRegistrationError(null);
    if (registrationAction === "sign_in") {
      navigate(getInternalEventRegistrationLoginUrl(activeEvent));
      return;
    }
    // CHI-20: internally Stripe-paid, non-special events buy tickets on their own page.
    if (registrationAction === "register" && shouldUseTicketsPage({ pricingPolicy, isSpecialEvent: activeEvent.isSpecialEvent === true })) {
      navigate(getEventTicketsUrl(activeEvent.slug));
      return;
    }
    if (registrationAction === "has_ended") {
      /* Nothing to attempt, and nothing to apologise for — it simply happened. */
      return;
    }
    if (registrationAction === "unavailable") {
      const message = "Registration is not currently available for this event.";
      setRegistrationError(message);
      chiNotify.warning("Registration is unavailable", {
        description: message,
        dedupeKey: `event-unavailable-${activeEvent.slug}`,
      });
      return;
    }
    if (registrationAction === "external_checkout" || registrationAction === "register") {
      const expectsCheckout = !isSpecialEvent && (
        registrationAction === "external_checkout" ||
        activeEvent.registrationMode === "paid" ||
        pricingPolicy?.pricingMode === "paid_internal");
      checkoutWindowRef.current = expectsCheckout ? reserveCheckoutWindow() : null;
      registrationMutation.mutate({ eventSlug: activeEvent.slug });
      return;
    }
    if (registrationAction === "external_registration" && externalRegistrationUrl) {
      window.location.assign(externalRegistrationUrl);
      return;
    }
    if (registrationAction === "join" && activeEvent.joinUrl) {
      window.open(activeEvent.joinUrl, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <div style={{ background: "#080C18", minHeight: "100vh" }}>
      <Navigation />

      {/* ── HERO ── */}
      <section
        className="event-detail-hero"
        style={{
          paddingTop: "120px",
          paddingBottom: "0",
          position: "relative",
          overflow: "hidden",
          minHeight: "520px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        {/* Hero background — gradient based on event color */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(135deg, #080C18 0%, ${accentColor}22 50%, #080C18 100%)`,
          }}
        />
        {/*
          Keep the complete feature image visible rather than cropping it to fill
          the hero. The mask dissolves its outer edges into the color layer below,
          avoiding a hard rectangular boundary at either side.
        */}
        <img
          className="event-detail-hero__art"
          aria-hidden="true"
          alt=""
          src={eventHeroArtwork.url}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            maskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,.08) 8%, rgba(0,0,0,.32) 16%, rgba(0,0,0,.72) 25%, black 34%, black 66%, rgba(0,0,0,.72) 75%, rgba(0,0,0,.32) 84%, rgba(0,0,0,.08) 92%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,.08) 8%, rgba(0,0,0,.32) 16%, rgba(0,0,0,.72) 25%, black 34%, black 66%, rgba(0,0,0,.72) 75%, rgba(0,0,0,.32) 84%, rgba(0,0,0,.08) 92%, transparent 100%)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, rgba(8,12,24,0.88) 0%, rgba(8,12,24,0.72) 45%, rgba(8,12,24,0.34) 100%), linear-gradient(0deg, #080C18 0%, rgba(8,12,24,0.05) 45%, rgba(8,12,24,0.38) 100%)`,
          }}
        />
        {/* Radial glow */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 60% 40%, ${accentColor}28 0%, transparent 60%)`,
          }}
        />
        {/* Subtle grid texture */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(${accentColor}08 1px, transparent 1px), linear-gradient(90deg, ${accentColor}08 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        {/* Bottom fade */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "120px", background: "linear-gradient(to bottom, transparent, #080C18)" }} />

        <div className="container" style={{ position: "relative", zIndex: 2, paddingBottom: "3rem" }}>
          <div className="event-hero-grid">
            <div className="event-hero-copy">
              {/* Back link */}
              <Link
                href="/calendar-of-events"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  color: "#64748B",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  marginBottom: "1.5rem",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = accentColor)}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#64748B")}
              >
                <ArrowLeft size={14} /> Back to Events
              </Link>

              {/* Type badge */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    padding: "0.35rem 0.875rem",
                    background: `${accentColor}20`,
                    border: `1px solid ${accentColor}50`,
                    borderRadius: "100px",
                    color: accentColor,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {isVirtual ? <Video size={12} /> : <Building2 size={12} />}
                  {isVirtual ? "Virtual Event" : "In-Person Event"}
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    padding: "0.35rem 0.875rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "100px",
                    color: "#94A3B8",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  <Calendar size={12} /> {event.displayDate}
                </span>
              </div>

              {/* Title */}
              <h1
                style={{
                  /*
                    Serif, per the reference design: the title is the one place the page
                    slows down, and a condensed display face was reading as a label rather
                    than a headline. Negative tracking because Georgia sets wide at this
                    size.
                  */
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontWeight: 400,
                  fontSize: "clamp(2.6rem, 5.4vw, 4.6rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.035em",
                  color: "white",
                  marginBottom: "0.75rem",
                  maxWidth: "780px",
                }}
              >
                {event.title}
              </h1>
              {event.subtitle && (
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.1rem", color: accentColor, fontWeight: 600, marginBottom: "1rem" }}>
                  {event.subtitle}
                </p>
              )}

              {/* Quick meta row */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", marginTop: "1rem" }}>
                {[
                  { icon: <Clock size={14} />, text: event.time },
                  { icon: <MapPin size={14} />, text: event.location },
                  { icon: <Users size={14} />, text: eventSpeakerSummary },
                ].map(({ icon, text }) => (
                  <span key={text} style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "0.875rem" }}>
                    <span style={{ color: accentColor }}>{icon}</span>
                    {text}
                  </span>
                ))}
              </div>
            </div>

            {/*
            The speaker, as the hero's counterweight to the title.

            A ring rather than a plain circle: the conic gradient is the brand's, and
            it distinguishes a person from the event artwork elsewhere on the page.
            Hidden from assistive tech only when there is no portrait — the alt text
            names who is speaking, which the meta row also says, so it is not the only
            route to that fact.
          */}
            <div
              className={`event-hero-speaker${eventSpeakers.length > 1 ? " event-hero-speaker--multiple" : ""}`}
              onMouseEnter={() => setHeroSpeakerPaused(true)}
              onMouseLeave={() => setHeroSpeakerPaused(false)}
              onFocusCapture={() => setHeroSpeakerPaused(true)}
              onBlurCapture={event => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) setHeroSpeakerPaused(false);
              }}
              aria-label={eventSpeakers.length > 1 ? `Speakers: ${eventSpeakerSummary}` : undefined}
            >
              {eventSpeakers.length === 1 ? (
                <div className="event-hero-orbit" style={{ "--event-accent": accentColor } as React.CSSProperties}>
                  <SpeakerFace
                    name={activeHeroSpeaker.name}
                    portraitUrl={activeHeroSpeaker.portraitUrl}
                    accent={accentColor}
                    radius="50%"
                  />
                </div>
              ) : (
                <div className="event-hero-speaker-stage" aria-hidden="true">
                  {eventSpeakers.map((speaker, index) => (
                    <div
                      className={`event-hero-orbit event-hero-orbit--slide${index === heroSpeakerIndex ? " is-active" : ""}`}
                      style={{ "--event-accent": accentColor } as React.CSSProperties}
                      key={speaker.id}
                    >
                      <SpeakerFace
                        name={speaker.name}
                        portraitUrl={speaker.portraitUrl}
                        accent={accentColor}
                        radius="50%"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section style={{ padding: "3rem 0 5rem" }}>
        <div className="container">
          <div className="event-detail-layout" style={{ display: "grid", gap: "2.5rem", alignItems: "start", "--accent": accentColor } as React.CSSProperties}>

            {/* ── CONTENT COLUMN ── */}
            <div>
              {/*
                Two views of the same event: what it is, and what there is to take
                away from it. Media used to sit at the bottom of a long scroll inside
                the speaker's section, where a reader had to already be looking for it.

                A real tablist, not two styled buttons: role=tab with
                aria-selected and aria-controls is what tells a screen reader these
                switch one region rather than navigate somewhere, and arrow-key
                movement between tabs is the behaviour that convention implies.
              */}
              <div className="event-tabs" role="tablist" aria-label="Event information" style={{marginBottom: "3rem"}}>
                {([
                  { key: "description" as const, label: "Description", icon: <Info size={26} strokeWidth={1.9} /> },
                  { key: "speaker" as const, label: eventSpeakers.length >= 2 ? "Speakers" : "Speaker", icon: <UserRound size={26} strokeWidth={1.9} /> },
                  /*
                    Named for what it holds. On a past event with a recording the
                    thing a visitor wants is the recording, and "Media & resources"
                    does not say that it is there.
                  */
                  { key: "media" as const, label: "Media & resources", icon: <Images size={26} strokeWidth={1.9} />, count: speakerGraphics.length + (hasResources ? 1 : 0) + eventDocuments.length, locked: !canAccessMedia },
                  ...(myEventTickets.showTab ? [{ key: "tickets" as const, label: "My Ticket(s)", icon: <Ticket size={26} strokeWidth={1.9} />, count: myEventTickets.activeTickets.length }] : []),
                ]).map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    id={`event-tab-${tab.key}`}
                    aria-selected={contentTab === tab.key}
                    aria-controls={`event-panel-${tab.key}`}
                    tabIndex={contentTab === tab.key ? 0 : -1}
                    data-active={contentTab === tab.key || undefined}
                    onClick={() => setContentTab(tab.key)}
                    onKeyDown={keyEvent => {
                      if (keyEvent.key !== "ArrowRight" && keyEvent.key !== "ArrowLeft") return;
                      keyEvent.preventDefault();
                      // Wraps at both ends, which is what a tablist is expected to do.
                      const order: Array<typeof contentTab> = ["description", "speaker", "media", ...(myEventTickets.showTab ? ["tickets" as const] : [])];
                      const step = keyEvent.key === "ArrowRight" ? 1 : -1;
                      setContentTab(current => order[(order.indexOf(current) + step + order.length) % order.length]);
                    }}
                  >
                    {/*
                      White at all times, including on an inactive tab whose label is
                      muted: the icon is the tab's landmark and greying it out with the
                      text made the rail read as three disabled controls.
                    */}
                    <span className="event-tabs__icon" aria-hidden="true">{tab.icon}</span>
                    {tab.label}
                    {"locked" in tab && tab.locked ? (
                      <span className="event-tabs__count" aria-label="Members only"><LockKeyhole size={13} strokeWidth={2.2} aria-hidden="true" /></span>
                    ) : tab.count ? <span className="event-tabs__count">{tab.count}</span> : null}
                  </button>
                ))}
              </div>

              <div
                className="event-tabpanel"
                role="tabpanel"
                id="event-panel-description"
                aria-labelledby="event-tab-description"
                hidden={contentTab !== "description"}
              >
                {/* About */}
                <div style={{ marginBottom: "2.5rem" }}>
                  <h2 style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.75rem", color: "white", marginBottom: "1.15rem" }}>
                    About This Event
                  </h2>
                  <p style={{ color: "#94A3B8", fontSize: "1rem", lineHeight: 1.85, fontFamily: "'Inter', sans-serif" }}>
                    {event.description}
                  </p>
                </div>

                {/*
                What the event promises, when the organiser has said. Both are
                optional, so each section is absent rather than an empty heading —
                a "Highlights" title over nothing reads as a page that failed to
                load rather than an event without bullets.
              */}
                {event.highlights?.length > 0 && (
                  <div style={{ marginBottom: "2.5rem" }}>
                    <h2 style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.75rem", color: "white", marginBottom: "1.15rem" }}>
                      What You'll Take Away
                    </h2>
                    <ul style={{ display: "grid", gap: ".7rem", margin: 0, padding: 0, listStyle: "none", maxWidth: "680px" }}>
                      {event.highlights.map(highlight => (
                        <li key={highlight} style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.7 }}>
                          <CheckCheck size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: ".2rem", color: event.color }} />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {event.agenda && event.agenda.length > 0 && (
                  <div style={{ marginBottom: "2.5rem" }}>
                    <h2 style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.75rem", color: "white", marginBottom: "1.15rem" }}>
                      Agenda
                    </h2>
                    {/*
                    A description list, not a table: it is pairs of time and what
                    happens, and a two-column table of four rows is a table only
                    in markup.
                  */}
                    <dl style={{ display: "grid", gap: ".1rem", margin: 0, maxWidth: "680px" }}>
                      {event.agenda.map(entry => (
                        <div key={`${entry.time}-${entry.item}`} style={{ display: "grid", gridTemplateColumns: "minmax(6.5rem, auto) 1fr", gap: "1rem", padding: ".7rem 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                          <dt style={{ color: event.color, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, whiteSpace: "nowrap" }}>{entry.time}</dt>
                          <dd style={{ margin: 0, color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>{entry.item}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

              </div>

              {/*
                Speaker panel. Its own view rather than a section at the foot of the
                event description: who is presenting is a distinct question from what
                the session is, and burying a biography under the agenda meant nobody
                reached it.
              */}
              <div
                className="event-tabpanel"
                role="tabpanel"
                id="event-panel-speaker"
                aria-labelledby="event-tab-speaker"
                hidden={contentTab !== "speaker"}
              >
                {/*
                About Speaker.

                Moved out of the sidebar, where it was a card competing with the event's
                own facts, into the content column where there is room for a biography
                and their media. The sidebar answers "can I come"; this answers "who am
                I coming to hear", which is a different question and a longer one.
              */}
                <div style={{ marginBottom: "2.5rem" }}>
                  <h2 style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.75rem", color: "white", marginBottom: "1.15rem" }}>
                    About the {eventSpeakers.length === 1 ? "Speaker" : "Speakers"}
                  </h2>

                  <div style={{ display: "grid", gap: "1rem" }}>
                    {eventSpeakers.map(speaker => (
                      <article
                        className={`event-about-speaker${eventSpeakers.length > 1 ? " event-about-speaker--card" : ""}`}
                        key={speaker.id}
                      >
                        <span className="event-about-speaker__face">
                          <SpeakerFace
                            name={speaker.name}
                            portraitUrl={speaker.portraitUrl}
                            accent={accentColor}
                            radius="18px"
                          />
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.35rem", color: "white", lineHeight: 1.15 }}>{speaker.name}</h3>
                          {(speaker.organization || speaker.professionalTitle) && (
                            <p style={{ margin: ".3rem 0 0", color: accentColor, fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
                              {[speaker.professionalTitle, speaker.organization].filter(Boolean).join(" · ")}
                            </p>
                          )}
                          {speaker.bio && (
                            <p className="event-about-speaker__bio">
                              {speaker.bio}
                            </p>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>

                </div>
              </div>

              {/*
                Media panel. Kept mounted-but-hidden rather than unmounted, so
                switching back does not re-fetch thumbnails or lose the strip's scroll
                position.
              */}
              {myEventTickets.showTab && (
                <div className="event-tabpanel" role="tabpanel" id="event-panel-tickets" aria-labelledby="event-tab-tickets" hidden={contentTab !== "tickets"}>
                  <EventTicketsPanel
                    tickets={myEventTickets.activeTickets}
                    eventTitle={event.title}
                    details={{ date: event.displayDate || event.date, time: event.time, venue: event.location }}
                    onGuestUpdated={() => void myEventTickets.reload()}
                  />
                </div>
              )}
              <div
                className="event-tabpanel"
                role="tabpanel"
                id="event-panel-media"
                aria-labelledby="event-tab-media"
                hidden={contentTab !== "media"}
              >
                {hasResources && (
                  <section aria-labelledby="event-member-resources-heading" style={{ marginTop: "1.5rem", marginBottom: speakerGraphics.length > 0 ? "2rem" : 0 }}>
                    <h2 id="event-member-resources-heading" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "1.75rem", color: "white", marginBottom: "1.15rem" }}>
                      Media & resources
                    </h2>
                    {!isLoggedIn || !isPremium ? (
                      <div style={{ padding: "1rem", borderRadius: ".8rem", border: "1px solid rgba(0,212,255,.2)", background: "rgba(0,212,255,.04)", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", lineHeight: 1.6 }}>
                        <LockKeyhole size={18} aria-hidden="true" style={{ verticalAlign: "text-bottom", marginRight: ".45rem", color: "#7DEAFF" }} />
                        Recordings and event attachments are available to members. Sign in with an active membership to access them.
                      </div>
                    ) : memberResourcesQuery.isLoading ? (
                      <p style={{ color: "#94A3B8" }}>Loading member resources…</p>
                    ) : memberResourcesQuery.isError ? (
                      <p role="alert" style={{ color: "#FDA4AF" }}>{memberResourcesQuery.error.message}</p>
                    ) : memberResources?.access !== "granted" ? (
                      <div role="status" style={{ padding: "1rem", borderRadius: ".8rem", border: "1px solid rgba(0,212,255,.2)", color: "#CBD5E1" }}>These resources are not available with this membership.</div>
                    ) : (
                      <div style={{ display: "grid", gap: "1.25rem" }}>
                        {memberResources.resources.map(resource => resource.kind === "video" && resource.videoUrl ? (
                          <div key={resource.id}>
                            <h3 style={{ margin: "0 0 .65rem", color: "#E2E8F0", fontSize: "1rem" }}>{resource.label}</h3>
                            <EventVideoPlayer url={resource.videoUrl} title={resource.providerTitle || resource.label} />
                          </div>
                        ) : (
                          <a key={resource.id} href={resource.downloadUrl ?? undefined} style={{ display: "flex", alignItems: "center", gap: ".7rem", padding: ".85rem", borderRadius: ".7rem", border: "1px solid rgba(255,255,255,.12)", color: "#E2E8F0", textDecoration: "none" }}>
                            <FileText size={21} aria-hidden="true" color={accentColor} />
                            <span style={{ minWidth: 0, flex: 1 }}><strong style={{ display: "block" }}>{resource.label}</strong><small style={{ color: "#94A3B8" }}>{resource.contentType?.split("/").pop()?.toUpperCase() || "Document"}{resource.sizeBytes ? ` · ${(resource.sizeBytes / 1024 / 1024).toFixed(1)} MB` : ""}</small></span>
                            <Download size={18} aria-label={`Download ${resource.label}`} />
                          </a>
                        ))}
                      </div>
                    )}
                  </section>
                )}
                {!canAccessMedia && (
                  <div style={{ marginTop: "1.5rem" }}>
                    <MembershipLockNotice what="Media & resources" isSignedIn={isAuthenticated} returnTo={`/events/${event.slug}`} />
                  </div>
                )}
                {!hasResources && eventDocuments.length === 0 && speakerGraphics.length === 0 && (
                  <EventMediaEmptyState hasPassed={eventHasPassed} />
                )}
                {eventDocuments.length > 0 && (
                  <div style={{ marginTop: "1.5rem", marginBottom: "2rem" }}>
                    <EventDocumentList
                      documents={eventDocuments}
                      canDownload={canAccessMedia}
                      isSignedIn={isAuthenticated}
                      eventSlug={event.slug}
                    />
                  </div>
                )}
                {/*
                  Their media, as a slider. A grid of print-proportioned flyers pushed
                  the rest of the page down the further a speaker uploaded; a strip
                  keeps the section one height however many there are, and the lightbox
                  is where a file is actually read.
                */}
                {speakerGraphics.length > 0 && (
                  <div style={{ marginTop: eventDocuments.length > 0 ? 0 : "1.5rem" }}>
                    <EventMediaHeading icon={Images}>Media & resources</EventMediaHeading>
                    <MediaCarousel
                      items={speakerGraphics.map(graphic => ({
                        url: graphic.url,
                        label: graphic.speakerName ? `Shared by ${graphic.speakerName}` : event.title,
                        contentType: graphic.contentType,
                      }))}
                    />
                  </div>
                )}
              </div>

            </div>

            {/* ── RIGHT SIDEBAR ── */}
            <div className="event-detail-sidebar" style={{ position: "sticky", top: "100px" }}>
              {/*
                Save to Calendar. Downloads a .ics, which is the format Outlook, Apple
                Calendar and Google all import — one file rather than a per-provider
                menu of "Add to Google / Add to Outlook" links.

                Fetched on click rather than prefetched: most visitors never press it,
                and the file is generated per event on the server.
              */}
              {/* Only for an event that has not happened: a calendar file for a past date is noise. */}
              {!eventHasPassed && (
                <button
                  type="button"
                  onClick={() => void saveToCalendar()}
                  disabled={savingInvite}
                  className="event-detail-calendar-button"
                  style={{ "--accent": accentColor } as React.CSSProperties}
                >
                  <CalendarPlus size={15} aria-hidden="true" />
                  {savingInvite ? "Preparing…" : "Save to Calendar"}
                  <span className="event-detail-calendar-button__hint">.ics</span>
                </button>
              )}

              {/* Registration card */}
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${accentColor}30`,
                  borderRadius: "1.25rem",
                  padding: "1.75rem",
                  marginBottom: "1rem",
                  boxShadow: `0 8px 40px ${accentColor}12`,
                }}
              >
                <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.25rem", color: "white", letterSpacing: "0.04em", marginBottom: "1.25rem" }}>
                  Event Registration
                </h3>

                {/* Detail rows */}
                {[
                  { icon: <Calendar size={15} />, label: "Date", value: event.displayDate },
                  { icon: <Clock size={15} />, label: "Time", value: event.time },
                  { icon: <MapPin size={15} />, label: "Location", value: event.location },
                  { icon: <Users size={15} />, label: "Format", value: isVirtual ? "Virtual / Online" : "In-Person" },
                  { icon: <Ticket size={15} />, label: "Registration", value: ticketCardPriceText(ticketCard, formatCents) ?? (isInternalCheckoutUnavailable ? `Paid · unavailable · ${registrationLabel}` : isExternalPaid ? `Paid · ${registrationLabel}` : `Free · ${registrationLabel}`) },
                ].map(({ icon, label, value }) => (
                  <div key={label} style={{ display: "flex", gap: "0.875rem", marginBottom: "1rem", alignItems: "flex-start" }}>
                    <span style={{ color: accentColor, flexShrink: 0, marginTop: "0.1rem" }}>{icon}</span>
                    <div>
                      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#475569", marginBottom: "0.15rem" }}>
                        {label}
                      </div>
                      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.875rem", color: "#CBD5E1" }}>{value}</div>
                    </div>
                  </div>
                ))}

                {/* Register CTA */}
                {ticketCard.kind !== "legacy" ? <TicketRegistrationCta state={ticketCard} accentColor={accentColor} onGetTickets={() => navigate(getEventTicketsUrl(activeEvent.slug))} onViewTickets={() => setContentTab(MY_TICKETS_TAB_KEY)} buyTicketsSlug={cardBuyTicketsSlug} completePurchase={cardCompletePurchase} /> : (
                <div className={registrationAction === "sign_in" ? "chi-cta-pair chi-cta-pair--stacked" : undefined}>
                  <button
                    onClick={handleRegistrationAction}
                    disabled={ctaInactive || ctaBusy}
                    aria-disabled={ctaInactive || ctaBusy}
                    className={registrationAction === "sign_in" ? "chi-btn-primary" : undefined}
                    style={{
                      width: "100%",
                      padding: "0.9rem",
                      background: ctaInactive
                        ? "rgba(148,163,184,0.10)"
                        : registrationAction === "sign_in"
                          ? undefined
                          : registrationAction === "join"
                            ? "linear-gradient(135deg, #00B4D8, #0077B6)"
                            : registered
                              ? "rgba(34,197,94,0.15)"
                              : `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                      border: ctaInactive
                        ? "1px solid rgba(148,163,184,0.22)"
                        : registrationAction === "sign_in" ? undefined
                          : registrationAction === "join" ? "1px solid rgba(103,232,249,0.5)" : registered ? "1px solid rgba(34,197,94,0.4)" : "none",
                      borderRadius: "0.625rem",
                      color: ctaInactive ? "#CBD5E1" : registrationAction === "sign_in" ? "#FFFFFF" : registrationAction === "join" ? "#FFFFFF" : registered ? "#4ADE80" : "#080C18",
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      cursor: ctaInactive || ctaBusy ? "not-allowed" : "pointer",
                      opacity: awaitingDecision && !canReapply ? 0.72 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      transition: "all 0.2s ease",
                      boxShadow: ctaInactive ? "none" : registrationAction === "sign_in" ? undefined : registrationAction === "join" ? "0 6px 20px rgba(0,180,216,0.35)" : registered || ctaBusy ? "none" : `0 6px 20px ${accentColor}40`,
                    }}
                  >
                    {registrationAction === "has_ended" ? (
                      <>This Event Has Ended <CtaIcon icon={CalendarCheck} /></>
                    ) : registrationAction === "unavailable" ? (
                      <>Registration Unavailable <CtaIcon icon={Ticket} /></>
                    ) : reapplyMutation.isPending ? (
                      <>Submitting… <CtaIcon icon={Ticket} /></>
                    ) : canReapply ? (
                      <>Reapply <CtaIcon icon={Ticket} /></>
                    ) : awaitingDecision && (approvalStatus === "pending" || registrationAction === "pending_review") ? (
                      <>Awaiting Approval <CtaIcon icon={Clock} /></>
                    ) : awaitingDecision && approvalStatus === "waitlisted" ? (
                      <>On the Waitlist <CtaIcon icon={Clock} /></>
                    ) : registrationMutation.isPending ? (
                      <>Preparing Registration… <CtaIcon icon={Ticket} /></>
                    ) : registrationAction === "join" ? (
                      <>Join on Zoom <CtaIcon icon={Video} /></>
                    ) : registered ? (
                      <>You're Registered! <CtaIcon icon={CheckCheck} /></>
                    ) : registrationAction === "sign_in" ? (
                      <>Sign In to Register <CtaIcon icon={Ticket} /></>
                    ) : isExternalPaid ? (
                      <>Continue to Secure Checkout <CtaIcon icon={ExternalLink} /></>
                    ) : externalRegistrationUrl ? (
                      <>Continue to Registration <CtaIcon icon={ExternalLink} /></>
                    ) : (
                      <>Request to Attend <CtaIcon icon={Ticket} /></>
                    )}
                  </button>
                  {registrationAction === "sign_in" && (
                    <button
                      type="button"
                      onClick={() => navigate(getInternalEventRegistrationCreateAccountUrl(activeEvent))}
                      className="chi-btn-outline"
                      style={{
                        width: "100%", padding: "0.8rem", fontSize: "0.78rem",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                      }}
                    >
                      Create a Member Account <CtaIcon icon={Users} />
                    </button>
                  )}
                </div>
                )}
                {registrationAction === "sign_in" && !cardBuyTicketsSlug && (
                  <p style={{ margin: ".65rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".75rem", lineHeight: 1.45, textAlign: "center" }}>
                    Create a member profile, then return here to register.
                  </p>
                )}
                {registrationError && (
                  <p role="alert" style={{ margin: ".75rem 0 0", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                    {registrationError}
                  </p>
                )}
                {waitlisted && (
                  <p
                    data-testid="event-detail-waitlist-copy"
                    role="status"
                    style={{ margin: ".75rem 0 0", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}
                  >
                    {WAITLIST_REGISTRANT_COPY}
                  </p>
                )}
                {ticketCard.kind === "legacy" && awaitingDecision && !canReapply && !waitlisted && (
                  <p role="status" style={{ margin: ".75rem 0 0", color: "#FCD34D", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                    Your registration is awaiting approval. The event link will appear here once it is approved.
                  </p>
                )}
                {(ticketCard.kind === "legacy" ? registered : ticketCard.kind === "registered") && isVirtual && (
                  <p style={{ margin: ".75rem 0 0", color: activeEvent.joinUrl ? "#A5F3FC" : "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
                    {activeEvent.joinUrl
                      ? "Your secure Zoom link is ready. It is shown only to successfully registered attendees."
                      : "Your secure Zoom link will appear here after successful registration once the event meeting is ready."}
                  </p>
                )}
                {pricingPolicy?.publicNotice && (
                  <p style={{ margin: ".75rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>{pricingPolicy.publicNotice}</p>
                )}
                {ticketCard.kind === "legacy" && isInternalCheckoutUnavailable && !pricingPolicy?.publicNotice && (
                  <p style={{ margin: ".75rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>Internal checkout is not yet available for this event. Please check back for registration details.</p>
                )}
              </div>

              {/*
                Share card. Real platform buttons rather than a URL field and one
                "Copy Event Link": people share an event to a place, not to a
                clipboard, and making them copy-then-paste was an extra two steps
                before the thing they actually wanted.
              */}
              {/* Sharing invites people to something. A finished event is not an invitation. */}
              {!eventHasPassed && (
                <div
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "1rem",
                    padding: "1.25rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.85rem", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748B" }}>
                    <Share2 size={12} /> Share This Event
                  </div>
                  <SocialShareButtons
                    url={eventUrl}
                    title={event.title}
                    accentColor={accentColor}
                    onCopied={() => chiNotify.success("Event link copied", {
                      description: "The event page link is ready to share.",
                      dedupeKey: `event-link-${slug}`,
                    })}
                  />
                </div>
              )}

              {/* View all events link */}
              <Link
                href="/calendar-of-events"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  padding: "0.75rem",
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "0.75rem",
                  color: "#64748B",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${accentColor}40`; e.currentTarget.style.color = accentColor; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#64748B"; }}
              >
                View All Events <CtaIcon icon={Calendar} size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .event-detail-hero__art {
          position:absolute;
          z-index:0;
          opacity:.58;
          display:block;
          width:auto;
          max-width:none;
          height:116%;
          object-fit:contain;
          object-position:center top;
          filter:saturate(.9) contrast(1.04);
          transform:translate(-50%,-50%) scale(1.02);
        }

        .event-detail-layout {
          grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
        }

        /*
          Hero: copy on the left, speaker on the right. A fixed right track rather than
          a fraction, so the portrait is the same size whatever the title's length —
          a person's face resizing with a headline reads as a bug.
        */
        .event-hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 330px;
          gap: clamp(2rem, 4vw, 4rem);
          align-items: center;
        }

        .event-hero-copy { min-width: 0; }
        .event-hero-speaker { display: flex; justify-content: center; }
        .event-hero-speaker--multiple { width: 100%; }

        .event-hero-speaker-stage {
          position: relative;
          width: 320px;
          height: 310px;
        }

        .event-hero-speaker-stage .event-hero-orbit--slide {
          position: absolute;
          top: 15px;
          left: 0;
          width: 280px;
          opacity: 0;
          transform: scale(.985);
          pointer-events: none;
          transition:
            opacity .7s ease-in-out,
            transform .7s cubic-bezier(.22, 1, .36, 1);
        }

        .event-hero-speaker-stage .event-hero-orbit--slide.is-active {
          z-index: 1;
          opacity: 1;
          transform: scale(1);
        }

        /*
          A glass ring rather than the four-colour conic gradient it replaced.

          That gradient put a saturated rainbow around every speaker's face, which
          competed with the portrait it was framing and with the accent colour the
          rest of the page is keyed to. This is a frosted rim instead: a translucent
          fill, a hairline highlight, and a wide soft shadow to lift it off the
          background. The only colour is a trace of the event's own accent in the
          outer glow, so the frame belongs to the page without shouting over the face.
        */
        .event-hero-orbit {
          position: relative;
          width: min(280px, 100%);
          aspect-ratio: 1;
          padding: 10px;
          border-radius: 50%;
          background: linear-gradient(150deg, rgba(255,255,255,.13), rgba(255,255,255,.03) 46%, rgba(255,255,255,.08));
          border: 1px solid rgba(255,255,255,.14);
          /* Frosts whatever sits behind the rim, which is what makes it read as glass. */
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.22),
            0 24px 60px rgba(0,0,0,.45),
            0 0 60px color-mix(in srgb, var(--event-accent, #E91E8C) 14%, transparent);
        }

        /* An inner hairline, so the photo has an edge instead of floating in the rim. */
        .event-hero-orbit::after {
          content: "";
          position: absolute;
          inset: 10px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.12);
          pointer-events: none;
        }

        .event-hero-orbit img {
          width: 100%;
          height: 100%;
          border-radius: inherit;
          object-fit: cover;
          /* Faces sit above centre in a portrait crop. */
          object-position: center 18%;
          background: #151A2A;
        }

        /*
          One column below 980px, with the portrait first: on a phone the face is the
          fastest way to recognise whose talk this is, and stacking it under a
          four-line title buries it.
        */
        @media (max-width: 980px) {
          .event-hero-grid { grid-template-columns: minmax(0, 1fr); }
          .event-hero-speaker { order: -1; justify-content: flex-start; }
          .event-hero-orbit { width: 210px; }
          .event-hero-speaker-stage { width: min(250px, 88vw); height: 240px; }
          .event-hero-speaker-stage .event-hero-orbit--slide { top: 14px; width: 210px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .event-hero-speaker-stage .event-hero-orbit--slide { transition: none; }
        }

        /*
          The content tabs, as a glass rail.

          Three things do the work, and all three are needed — any one alone reads as a
          flat pill. backdrop-filter frosts the page showing through, so the rail has
          depth rather than a fill colour. The inset top highlight is the lit edge you
          get where light catches the lip of real glass. And the outer shadow separates
          the rail from the page so it sits *above* it instead of being painted on.
        */
        .event-tabs {
          position: relative;
          display: inline-flex;
          gap: .25rem;
          margin-bottom: 15px;
          padding: .46rem;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: 999px;
          background:
            linear-gradient(148deg, rgba(255,255,255,.11), rgba(255,255,255,.02) 44%, rgba(255,255,255,.07));
          backdrop-filter: blur(18px) saturate(1.35);
          -webkit-backdrop-filter: blur(18px) saturate(1.35);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.24),
            inset 0 -1px 0 rgba(255,255,255,.06),
            0 12px 30px rgba(0,0,0,.34);
          overflow: hidden;
        }

        /*
          A sheen across the top half. Skewed and pointer-events-none, so it is purely
          a highlight and never intercepts a press.
        */
        .event-tabs::before {
          content: "";
          position: absolute;
          inset: 0 0 55% 0;
          background: linear-gradient(180deg, rgba(255,255,255,.10), transparent);
          pointer-events: none;
        }

        .event-tabs button {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: .45rem;
          /* 20% larger than the 42px/1.25rem this started at. */
          min-height: 58px;
          padding: 0 1.6rem;
          border: 1px solid transparent;
          border-radius: 999px;
          background: transparent;
          color: #9AA5BC;
          font-family: 'Space Grotesk', sans-serif;
          font-size: var(--chi-body-compact);
          font-weight: 700;
          letter-spacing: .01em;
          white-space: nowrap;
          cursor: pointer;
          transition: color 200ms ease, background 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
        }

        /* Inactive hover frosts a little rather than colouring in. */
        .event-tabs button:hover:not([data-active]) {
          color: #EDF1F8;
          background: rgba(255,255,255,.06);
          border-color: rgba(255,255,255,.10);
        }

        /*
          Active: tinted glass rather than a solid fill, so the rail stays one material
          and the selected tab reads as the lit pane in it.
        */
        .event-tabs button[data-active] {
          color: #fff;
          border-color: color-mix(in srgb, var(--accent) 52%, rgba(255,255,255,.2));
          background:
            linear-gradient(150deg,
              color-mix(in srgb, var(--accent) 42%, transparent),
              color-mix(in srgb, var(--accent) 16%, rgba(255,255,255,.06)));
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.30),
            0 8px 22px color-mix(in srgb, var(--accent) 26%, transparent);
        }

        /*
          Animated on state change, which is the part of a Lordicon-style set that
          actually reads as "modern" — the motion, not the artwork. Done with CSS on
          icons already bundled, so there is no third-party script executing on the
          page and no CDN request that can fail and leave a tab with no mark.
        */
        /*
          Breathing room between the rail and whatever it switched to. Padding on the
          panel rather than margin on the rail, so the gap belongs to the content and
          is the same whichever tab is showing — a margin below the tabs would be
          collapsed away by whichever element happened to lead each panel.

          The side padding is deliberate too: without it the panel's text sat flush
          against the column edge while the rail was visibly inset, and the two read as
          belonging to different grids.
        */
        .event-tabpanel {
          /*
            No top padding: the rail's own 15px margin is the whole gap now. Leaving
            both would stack them into roughly 40px, which is not what "15px" asked
            for. The side padding stays — it aligns the panel's text with the rail,
            which is inset by its own padding.
          */
          padding: 0 .35rem;
        }

        .event-tabpanel[hidden] { display: none; }

        .event-tabs__icon {
          display: grid;
          place-items: center;
          color: #FFFFFF;
          transition: transform 320ms cubic-bezier(.34, 1.56, .64, 1), opacity 220ms ease;
        }

        /* Slightly held back when the tab is not selected, but still white. */
        .event-tabs button:not([data-active]) .event-tabs__icon { opacity: .78; }

        /* Overshoots and settles, so selecting a tab feels like a press landing. */
        .event-tabs button[data-active] .event-tabs__icon {
          animation: event-tab-icon-pop 420ms cubic-bezier(.34, 1.56, .64, 1);
        }

        .event-tabs button:hover:not([data-active]) .event-tabs__icon {
          transform: translateY(-1px) scale(1.08);
          opacity: 1;
        }

        @keyframes event-tab-icon-pop {
          0% { transform: scale(.72) rotate(-12deg); opacity: .5; }
          62% { transform: scale(1.16) rotate(3deg); opacity: 1; }
          100% { transform: none; opacity: 1; }
        }

        /* Motion is decoration here, so it goes entirely for anyone who opts out. */
        @media (prefers-reduced-motion: reduce) {
          .event-tabs__icon { transition: none; }
          .event-tabs button[data-active] .event-tabs__icon { animation: none; }
          .event-tabs button:hover:not([data-active]) .event-tabs__icon { transform: none; }
        }

        /* How many files are behind the tab, so it is worth pressing or clearly not. */
        .event-tabs__count {
          display: grid;
          place-items: center;
          min-width: 21px;
          height: 21px;
          padding: 0 .38rem;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,.16);
          background: rgba(5,7,13,.42);
          font-size: .68rem;
          font-weight: 800;
        }

        .event-tabs button:not([data-active]) .event-tabs__count {
          background: rgba(255,255,255,.07);
          color: #CBD5E1;
        }

        @media (max-width: 520px) {
          .event-tabs { display: flex; width: 100%; }
          .event-tabs button { flex: 1; justify-content: center; padding: 0 .6rem; }
        }

        /* About the Speaker: face beside the biography, in the content column. */
        .event-about-speaker {
          display: grid;
          grid-template-columns: 104px minmax(0, 1fr);
          /*
             Wider than the 1.15rem it started at: at that gap the biography began
             almost against the portrait's edge, so the two read as one block and
             the first line of text was hard to find. 2.5rem gives the face its own
             space without stranding the text away from the person it describes.
          */
          gap: 2.5rem;
          align-items: start;
        }

        .event-about-speaker--card {
          padding: clamp(1rem, 2.5vw, 1.35rem);
          border: 1px solid color-mix(in srgb, var(--accent) 24%, rgba(255,255,255,.14));
          border-radius: 1rem;
          background: linear-gradient(145deg, color-mix(in srgb, var(--accent) 5%, rgba(13,18,32,.88)), rgba(8,12,24,.72));
          box-shadow: inset 0 1px 0 rgba(255,255,255,.04), 0 12px 28px rgba(0,0,0,.14);
        }

        /* Fixed box; SpeakerFace fills it with either a photo or initials. */
        .event-about-speaker__face {
          display: block;
          width: 104px;
          height: 104px;
        }

        .event-about-speaker__bio {
          margin: .8rem 0 0;
          padding: clamp(.75rem, 2vw, 1rem);
          border-radius: .8rem;
          color: #AEB8C9;
          background: linear-gradient(145deg, color-mix(in srgb, var(--accent) 7%, rgba(12,18,32,.72)), rgba(8,12,24,.58));
          box-shadow: inset 0 1px 0 rgba(255,255,255,.035);
          font-family: 'Inter', sans-serif;
          font-size: var(--chi-body-compact);
          line-height: 1.7;
        }

        /* The face stacks above the biography before the two would fight for width. */
        @media (max-width: 560px) {
          .event-about-speaker { grid-template-columns: minmax(0, 1fr); }
        }

        /*
          Filled in the event's accent, and first in the column above the registration
          card. It was a muted outline button at the bottom of the sidebar, which put
          the second thing most people want — getting the date into their calendar —
          below three cards and styled as an afterthought.

          It stays visually lighter than Request to Attend inside the card below:
          that one is the gradient, this is a flat fill, so the primary action is
          still the primary action.
        */
        .event-detail-calendar-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: .55rem;
          width: 100%;
          min-height: 50px;
          margin-bottom: 1rem;
          padding: .8rem;
          border: 1px solid color-mix(in srgb, var(--accent) 60%, transparent);
          border-radius: .85rem;
          background: color-mix(in srgb, var(--accent) 22%, rgba(13,18,32,.9));
          color: #fff;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .86rem;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 10px 26px color-mix(in srgb, var(--accent) 20%, transparent);
          transition: border-color .2s ease, background .2s ease, transform .2s ease, box-shadow .2s ease;
        }

        .event-detail-calendar-button:hover:not(:disabled),
        .event-detail-calendar-button:focus-visible:not(:disabled) {
          border-color: var(--accent);
          background: color-mix(in srgb, var(--accent) 34%, rgba(13,18,32,.9));
          transform: translateY(-1px);
          box-shadow: 0 16px 34px color-mix(in srgb, var(--accent) 28%, transparent);
        }

        .event-detail-calendar-button:disabled { cursor: wait; opacity: .6; }

        /* Names the format without making it the label. */
        .event-detail-calendar-button__hint {
          padding: .1rem .35rem;
          border-radius: .3rem;
          background: rgba(0,0,0,.28);
          color: rgba(255,255,255,.75);
          font-size: .62rem;
          letter-spacing: .06em;
        }

        @media (max-width: 900px) {
          .event-detail-layout {
            grid-template-columns: minmax(0, 1fr);
            gap: 1.5rem !important;
          }

          .event-detail-sidebar {
            position: static !important;
            top: auto !important;
          }


          .event-detail-hero__art {
            width:116%;
            height:auto;
            object-position:center top;
            opacity:.47;
            transform:translate(-50%,-50%) scale(1.02);
          }
        }

        @media (max-width: 520px) {
          .event-detail-speaker {
            flex-direction: column;
          }


          .event-detail-hero__art {
            object-position:center top;
            opacity:.38;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
}
